import { supabase } from '@/integrations/supabase/client';

export type CouponDiscountType =
  | 'percentage'
  | 'fixed'
  | 'free_shipping'
  | 'shipping_percentage'
  | 'shipping_fixed';

/** Cupons que incidem sobre o FRETE (e não sobre os itens do pedido). */
export const isShippingCouponType = (type?: string | null): boolean =>
  type === 'free_shipping' || type === 'shipping_percentage' || type === 'shipping_fixed';

interface CouponLike {
  discount_type: string;
  discount_value: number | string;
}

/**
 * Desconto em R$ de um cupom:
 *  - percentage / fixed: sobre o subtotal dos itens;
 *  - free_shipping: o frete inteiro;
 *  - shipping_percentage / shipping_fixed: parte do frete (nunca passa do valor do frete).
 */
export const calculateCouponDiscount = (
  coupon: CouponLike,
  amounts: { subtotal: number; freight: number },
): number => {
  const value = Number(coupon.discount_value) || 0;
  const subtotal = Math.max(0, amounts.subtotal);
  const freight = Math.max(0, amounts.freight);
  let discount: number;
  switch (coupon.discount_type) {
    case 'percentage':
      discount = subtotal * (Math.min(Math.max(value, 0), 100) / 100);
      break;
    case 'free_shipping':
      discount = freight;
      break;
    case 'shipping_percentage':
      discount = freight * (Math.min(Math.max(value, 0), 100) / 100);
      break;
    case 'shipping_fixed':
      discount = Math.min(value, freight);
      break;
    default:
      discount = Math.min(value, subtotal);
  }
  return Math.round(discount * 100) / 100;
};

/** Texto curto do tipo/valor do cupom, para listas e resumo do pedido. */
export const describeCoupon = (coupon: CouponLike): string => {
  const value = Number(coupon.discount_value) || 0;
  switch (coupon.discount_type) {
    case 'percentage':
      return `${value}%`;
    case 'free_shipping':
      return 'Frete grátis';
    case 'shipping_percentage':
      return `${value}% no frete`;
    case 'shipping_fixed':
      return `R$ ${value.toFixed(2)} no frete`;
    default:
      return `R$ ${value.toFixed(2)}`;
  }
};

/**
 * Cupom de frete (se o código for de um): o desconto gravado em events.discount_value acompanha o frete vigente.
 * Quem recalcula o total depois de mudar o frete (novo CEP) precisa recalcular o desconto.
 */
export async function fetchShippingCoupon(
  code?: string | null,
  supplierId?: string | null,
): Promise<CouponLike | null> {
  const trimmed = String(code ?? '').trim();
  if (!trimmed) return null;
  try {
    let query = supabase.from('discount_coupons').select('discount_type, discount_value').ilike('code', trimmed);
    if (supplierId) query = query.eq('supplier_id', supplierId);
    const { data } = await query.maybeSingle();
    return data && isShippingCouponType(data.discount_type) ? (data as CouponLike) : null;
  } catch {
    return null;
  }
}
